import axios from "axios";
import { makeAutoObservable } from "mobx";

interface TableJson {
  table_header: string[];
  table_content: { [key: string]: string | number }[];
}

export enum Step {
  CLEAR = 'clear',
  CREATE_ROUTE = 'createRoute',
  LIST_PRACTITIONERS = 'listPractitioners'
}

interface OllamaApiResponse {
  model: string;
  created_at: string;
  response: string;
  text: string;
  vid: string;
  pic: string;
  waiting: boolean;
  done: boolean;
  questions: string[];
  table_json: TableJson;
  render_table: boolean;
}

interface llmResponse {
  success: boolean;
  error_msg: string;
  payload: OllamaApiResponse;
}

interface ChatEntry {
  message: string;
  tableJson?: TableJson;
  messageId?: string;
}

interface UserData {
  userName: string;
  userFriendlyName: string; 
  userPicture: string;
  impersonateList: string[];
  isAdmin: boolean;
  earlyRelease: boolean;
}

interface Route {
  waypoints: any[];
  distance: number;
  duration: number;
  geometry: any;
}

interface CheckIn {
  notes?: string;
  followUp?: string;
  practitioner_id?: number;
  practitioner_name?: string;
}

interface PractitionerGoals {
  ytd_sales: number;
  goal: number;
  goal_percentage: number;
}

interface AvailableProduct {
  id: number;
  unit_price: number;
  name: string;
  sku: string;
}

class Store {
  userInput = "";
  prompts: string[] = [""];
  promptsFeedback: string[] = [""];
  answers: ChatEntry[] = [];
  textDisplay = "";
  picURL = "";
  vidURL = "";
  renderCustom = false;
  heatmap: any = [];
  currProgress: number = 0.0;
  yearGoal: number = 0.0;
  refreshDataTime: string = '';
  currentRoute: Route | null = null;
  currentPractitioner: any | null = {};
  selectedPractitioners: any[] = [];
  practitionersFilteredByFMC: any[] = [];
  backButtonHistory: string[] = [];
  currentDirections: number[][] = [];
  initialLatLon: [number, number] | null = null;
  practitionersCarouselFilter: string = "Geo-Verified";
  practitionersFilterMultiSelectValue: string[] = ["Name", "Address", "City", "State", "Specialty", "Email", "Zipcode"];
  currentActiveRoute: any = null;
  

  myQuestions: string[] = [];  // Added to handle multiple questions
  checkIn: CheckIn | null = {}
  
  currentQuestionIndex: number = 0;
  waitingForAnswers: boolean = false;  

  user: UserData | null = null;
  impersonatedUser: string | null = null;  // This is an email

  // View-As info - matched by impersonatedUser
  impersonatedUserName: string | null = null;
  impersonatedUserNetsuiteID: number | null = null;
  impersonatedUserRsdName: string | null = null;
  impersonatedUserRsdEmail: string | null = null;
  impersonatedUserAlias: string | null = null; // This is an alias email
  
  
  routingModalStep: Step = Step.LIST_PRACTITIONERS;
  practitionerGoals: PractitionerGoals | null = null;
  // generalNotes: string | null = null;
  availableProducts: AvailableProduct[] = [];

  // Cache for practitioners and leads data
  practitionersCache: Record<string, any[]> = {};
  leadsCache: Record<string, any[]> = {};

  // Cache for search term
  cachedSearchTerm: string = '';

  // Cache for total count
  cachedTotalCount: number = 0;

  constructor() {
    makeAutoObservable(this);    
  }

  setCurrentActiveRoute(a: any) {
    this.currentActiveRoute = a;
  }

  setInitialLatLon(ll?: [number, number]) {
    if (!ll || ll.length < 2) {
      this.initialLatLon = null;
    } else {
      const [lat, lon] = ll;
      if (!lat || !lon) {
        this.initialLatLon = null;
      } else {
        this.initialLatLon = ll;
      }
    }
  }

  setCurrentDirections(ds: number[][]) {
    this.currentDirections = ds;
  }

  setAvailableProducts(av: AvailableProduct[]) {
    this.availableProducts = av;
  }

  // Cache management methods
  setPractitionersCache(page: number, data: any[]) {
    this.practitionersCache[page] = data;
  }

  getPractitionersCache(page: number): any[] | undefined {
    return this.practitionersCache[page];
  }

  setLeadsCache(page: number, data: any[]) {
    this.leadsCache[page] = data;
  }

  getLeadsCache(page: number): any[] | undefined {
    return this.leadsCache[page];
  }

  // Total count cache methods
  setCachedTotalCount(count: number) {
    this.cachedTotalCount = count;
  }

  getCachedTotalCount(): number {
    return this.cachedTotalCount;
  }

  clearPractitionersCache() {
    this.practitionersCache = {};
  }

  clearLeadsCache() {
    this.leadsCache = {};
  }

  clearAllCache() {
    this.practitionersCache = {};
    this.leadsCache = {};
    this.cachedTotalCount = 0;
  }

  // Search term cache methods
  setCachedSearchTerm(searchTerm: string) {
    this.cachedSearchTerm = searchTerm;
  }

  getCachedSearchTerm(): string {
    return this.cachedSearchTerm;
  }

  clearCachedSearchTerm() {
    this.cachedSearchTerm = '';
  }

  setPractitionersFilterMultiSelectValue(multiSelectValue: string[]) {
    this.practitionersFilterMultiSelectValue = multiSelectValue;
  }

  getPractitionersFilterMultiSelectValue(): string[] {
    return this.practitionersFilterMultiSelectValue;
  }

  setPractitionersCarouselFilter(filter: string) {
    this.practitionersCarouselFilter = filter;
  }

  getPractitionersCarouselFilter(): string {
    return this.practitionersCarouselFilter;
  }

  setPractitionersFilteredByFMC(practitioners: any[]) {
    this.practitionersFilteredByFMC = practitioners;
  }

  setCheckIn(newCheckIn: CheckIn) {
    this.checkIn = {...this.checkIn, ...newCheckIn}
  }

  clearCheckIn() {
    this.checkIn = null
  }

  setGeneralNotes(notes: string) {
    this.currentPractitioner.general_notes = notes;
  }

  setRoutingModalStep(step: Step) {
    this.routingModalStep = step;
  }

  addToRoute(practitioner: any) {
    if(this.selectedPractitioners.filter((pract) => pract.id === practitioner.id).length === 0) {
      // Preserve first and last location flags
      const firstLocation = this.selectedPractitioners[0]?.isFirstLocation ? this.selectedPractitioners[0] : null;
      const lastLocation = this.selectedPractitioners[this.selectedPractitioners.length - 1]?.isLastLocation ? this.selectedPractitioners[this.selectedPractitioners.length - 1] : null;
      const middlePractitioners = this.selectedPractitioners.filter(p => !p.isFirstLocation && !p.isLastLocation);
      // Add new practitioner to middle practitioners
      middlePractitioners.push(practitioner);
      // Reconstruct the array with preserved first and last locations
      this.selectedPractitioners = [
        firstLocation,
        ...middlePractitioners,
        lastLocation
      ].filter(item => item !== null);
    }
  }

  removeFromRoute(practitioner: any) {
    this.selectedPractitioners = this.selectedPractitioners.filter((pract) => pract.id !== practitioner.id);
    console.log('selectedPractitioners', this.selectedPractitioners.length)
  }
  
  setSelectedPractitioners(practitioners: any[]) {
    this.selectedPractitioners = practitioners;
  }

  setCurrentRoute(route: Route) {
    this.currentRoute = route;
  }

  setRefreshDataTime(time: string) {
    this.refreshDataTime = time;
  }

  setCurrentPractitioner(practitioner: any) {
    this.currentPractitioner = {
      ...practitioner,
    };
  }

  setCurrProgress(val: number) {
    this.currProgress = val;
  } 
  
  setYearGoal(val: number) {
    this.yearGoal = val;
  }

  setPractitionerGoals(goals: PractitionerGoals) {
    this.practitionerGoals = goals
  }

  setUser(user: UserData) {
    this.user = user;
  }

  setImpersonatedUser(user: string) {
    this.impersonatedUser = user;
    store.setAnswers(`## Good ${store.getTimeGreeting()}, ${user} \n I'm your Sales Assistant AI.  Here are some things I can do:`);

    this.setUserInput('');
    this.prompts= [""];
  }

  setImpersonatedUserName(val: string) {
    this.impersonatedUserName = val;
  }

  setImpersonatedNetsuiteID(val: number) {
    this.impersonatedUserNetsuiteID = val;
  }

  setImpersonatedRsdName(val: string) {
    this.impersonatedUserRsdName = val;
  }

  setImpersonatedRsdEmail(val: string) {
    this.impersonatedUserRsdEmail = val;
  }

  setImpersonatedUserAlias(val: string) {
    this.impersonatedUserAlias = val;
  }

  setUserInput(value: string) {
    this.userInput = value;
  }

  addPrompt(prompt: string) {
    this.prompts.push(prompt);
  }

  addPromptFeedback(feedback: string, position: number) {
    this.promptsFeedback[position] = feedback;
  }

  setAnswers(newAnswer: string) {
    this.answers = [{ message: newAnswer }];
  }

  addAnswer(answer: string | ChatEntry, is_interrupted: boolean = false) {
    console.log('is_interrupted:', is_interrupted)
    if (is_interrupted) {
      this.answers.push({ message: "*Sales assistant AI was stopped by user.*" });
      return;
    } 
    console.log('add answer:', answer)
    if (typeof answer === 'string') {
      this.answers.push({ message: answer });
    } else {
      this.answers.push(answer);
    }
  }

  setText(text: string) {
    this.textDisplay = text;
  }

  setPic(pic: string) {
    this.picURL = pic;
  }

  setVid(vid: string) {
    this.vidURL = vid;
  }

  setRenderCustom(val: boolean) {
    this.renderCustom = val;
  }

  setHeatmap(val: any) {
    this.heatmap = val;
  }

  setMyQuestions(questions: string[]) {
    this.myQuestions = questions;
  }

  setCurrentQuestionIndex(value: number) {
    this.currentQuestionIndex = value;
  }

  setWaitingForAnswers(value: boolean) {
    this.waitingForAnswers = value;
  }  

  getTimeGreeting(): string {
    const now = new Date();
    const hour = now.getHours();

    if (hour < 12) {
        return "morning";
    } else if (hour < 18) {
        return "afternoon";
    } else {
        return "evening";
    }
  }

  async sendMsgToLLMService(msg: string, user_name: string, resume: boolean, answers_array: string[]): Promise<OllamaApiResponse> {

    try {
      const response = await axios.post<llmResponse>('http://127.0.0.1:8503/api/call_dummy',
        {
          'message': msg,
          'user_name': 'admin',
          'resume': resume,
          'answers_array': answers_array
        }
      );

      if (response.status >= 200 && response.status < 300 && response.data.success === true) {
        const result: OllamaApiResponse = response.data.payload;

        return result;
      } else {
        console.error('Server error:', response.status, response.statusText);
        return {
          model: '',
          created_at: '',
          response: "There is an issue pulling your data.  In the meantime, can you try another prompt?",
          text: '',
          vid: 'nan',
          pic: 'nan',
          waiting: false,
          done: false,
          questions: [],
          table_json: {
            table_header: [],
            table_content: [{}],
          },
          render_table: false,
        };
      }

    } catch (error: any) {
      console.error('Network error:', error);
      return {
        model: '',
        created_at: '',
        response: "I'm not connected to the internet -- I can't answer your question at this point...",
        text: '',
        vid: '',
        pic: '',
        waiting: false,
        done: false,
        questions: [],
        table_json: {
          table_header: [],
          table_content: [{}],
        },
        render_table: false,
      };
    }
  }

  addToBackButtonHistory(path: string) {
    this.backButtonHistory.push(path);
  }
  popBackButtonHistory() {
    this.backButtonHistory.pop();
  } 

}

export const store = new Store();